import React, { useState, useEffect } from 'react';
import { Table, Button, message, Modal, Input, DatePicker } from 'antd';
import { EyeOutlined, SearchOutlined } from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { resellerService } from '../../services/reseller.service';
import { RedemptionListItem, PaginatedRedemptionsResponse } from '../../types/reseller.types';
import Pagination from './Pagination';
import dayjs from 'dayjs';

const { RangePicker } = DatePicker;

interface RedemptionsTableProps {
  resellerId: number;
  recentRedemptions?: RedemptionListItem[];
  showPagination?: boolean;
}

const RedemptionsTable: React.FC<RedemptionsTableProps> = ({
  resellerId,
  recentRedemptions = [],
  showPagination = false
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState<PaginatedRedemptionsResponse | null>(null);
  const [page, setPage] = useState(1);
  const [showFullList, setShowFullList] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [dateRange, setDateRange] = useState<[string, string] | null>(null);
  const pageSize = 20;

  useEffect(() => {
    if (showPagination || showFullList) {
      loadData();
    }
  }, [showPagination, showFullList, page, resellerId, searchQuery, dateRange]);

  const loadData = async () => {
    setLoading(true);
    try {
      const response = await resellerService.getResellerRedemptions(
        resellerId,
        page,
        pageSize,
        searchQuery || undefined,
        dateRange?.[0],
        dateRange?.[1]
      );
      if (response.isSuccess && response.responseObject) {
        setData(response.responseObject);
      } else {
        message.error('Failed to load redemptions');
      }
    } catch (error) {
      message.error('Failed to load redemptions');
    } finally {
      setLoading(false);
    }
  };

  const handleViewAll = () => {
    setShowFullList(true);
  };

  const handleCloseModal = () => {
    setShowFullList(false);
    setPage(1);
    setData(null);
    setSearchQuery('');
    setDateRange(null);
  };

  const handleSearchChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setSearchQuery(e.target.value);
    setPage(1);
  };

  const handleDateRangeChange = (dates: any) => {
    if (dates && dates[0] && dates[1]) {
      setDateRange([
        dates[0].format('YYYY-MM-DD'),
        dates[1].format('YYYY-MM-DD')
      ]);
    } else {
      setDateRange(null);
    }
    setPage(1);
  };

  const columns: ColumnsType<RedemptionListItem> = [
    {
      title: 'Full Code',
      dataIndex: 'fullCode',
      key: 'fullCode',
      width: 180,
      render: (code: string) => <span className="code-mono">{code}</span>
    },
    {
      title: 'Coupon Set',
      dataIndex: 'couponSetName',
      key: 'couponSetName',
      ellipsis: true
    },
    {
      title: 'Redeemed On',
      dataIndex: 'redeemedOn',
      key: 'redeemedOn',
      width: 180,
      render: (date: string) => new Date(date).toLocaleString()
    },
    {
      title: 'User Email',
      dataIndex: 'redeemedByEmail',
      key: 'redeemedByEmail',
      ellipsis: true
    }
  ];

  if (!showPagination && !showFullList) {
    return (
      <div className="recent-redemptions">
        <Table
          columns={columns}
          dataSource={recentRedemptions}
          rowKey="id"
          pagination={false}
          size="small"
          locale={{ emptyText: 'No redemptions yet' }}
        />
        {recentRedemptions.length > 0 && (
          <div style={{ marginTop: 16, textAlign: 'center' }}>
            <Button icon={<EyeOutlined />} onClick={handleViewAll}>
              View All Redemptions
            </Button>
          </div>
        )}
      </div>
    );
  }

  if (showFullList && !showPagination) {
    return (
      <Modal
        title="All Redemptions"
        open={showFullList}
        onCancel={handleCloseModal}
        footer={null}
        width={1000}
        style={{ top: 20 }}
        zIndex={99999}
      >
        <div className="modal-filters" style={{ marginBottom: 16 }}>
          <Input
            placeholder="Search by code or email..."
            prefix={<SearchOutlined />}
            value={searchQuery}
            onChange={handleSearchChange}
            style={{ width: 250 }}
            allowClear
          />
          <RangePicker
            onChange={handleDateRangeChange}
            style={{ width: 280 }}
            placeholder={['Start Date', 'End Date']}
          />
        </div>

        <Table
          columns={columns}
          dataSource={data?.items || []}
          rowKey="id"
          pagination={false}
          loading={loading}
          size="small"
        />
        {data && (
          <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
            <Pagination
              currentPage={data.currentPage}
              totalPages={data.totalPages}
              totalCount={data.totalCount}
              onPageChange={setPage}
              disabled={loading}
            />
          </div>
        )}
      </Modal>
    );
  }

  return (
    <div className="redemptions-table-paginated">
      <div className="modal-filters" style={{ marginBottom: 16 }}>
        <Input
          placeholder="Search by code or email..."
          prefix={<SearchOutlined />}
          value={searchQuery}
          onChange={handleSearchChange}
          style={{ width: 250 }}
          allowClear
        />
        <RangePicker
          onChange={handleDateRangeChange}
          style={{ width: 280 }}
          placeholder={['Start Date', 'End Date']}
        />
      </div>

      <Table
        columns={columns}
        dataSource={data?.items || []}
        rowKey="id"
        pagination={false}
        loading={loading}
        size="small"
      />
      {data && (
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'center' }}>
          <Pagination
            currentPage={data.currentPage}
            totalPages={data.totalPages}
            totalCount={data.totalCount}
            onPageChange={setPage}
            disabled={loading}
          />
        </div>
      )}
    </div>
  );
};

export default RedemptionsTable;
